/**
 * Diagnostics: trouble codes, readiness monitors and the freeze frame.
 *
 * Every code is explained, not just named: what it means, whether it is safe
 * to keep driving, what the driver may notice, what usually causes it and what
 * to check. Explanations come from the code's standard structure, so they work
 * for any make, and say so plainly when a code is the carmaker's own.
 */
import { useCallback, useMemo, useState } from 'react';
import {
  PID_BY_NUMBER, SEVERITY_LABELS, SEVERITY_RANK, STATUS_EXPLANATIONS, decodeVin, explainDtc,
  type Dtc, type DtcContext, type DtcExplanation, type DtcSeverity, type DtcStatus, type ObdSession,
  type ReadinessResult,
} from '@obd/core';
import { Modal } from './DashboardView';
import { formatValue } from './draw';

interface ExplainedDtc {
  dtc: Dtc;
  info: DtcExplanation;
}

const SEVERITY_TONES: Record<DtcSeverity, string> = {
  stop: 'critical',
  soon: 'warning',
  check: 'neutral',
  info: 'neutral',
};

const GROUPS: { status: DtcStatus; title: string; tone: string }[] = [
  { status: 'stored', title: 'Stored', tone: 'critical' },
  { status: 'pending', title: 'Pending', tone: 'warning' },
  { status: 'permanent', title: 'Permanent', tone: 'critical' },
];

export function DiagnosticsView({ session, connected }: { session: ObdSession; connected: boolean }): JSX.Element {
  const [dtcs, setDtcs] = useState<Dtc[] | null>(null);
  const [readiness, setReadiness] = useState<ReadinessResult | null>(null);
  const [freeze, setFreeze] = useState<{ key: string; label: string; value: string }[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmClear, setConfirmClear] = useState(false);

  const vin = session.status().vin;
  const manufacturer = vin ? decodeVin(vin)?.manufacturer : undefined;
  const fuelType = session.vehicle.fuelType;

  const explained = useMemo<ExplainedDtc[]>(() => {
    const ctx: DtcContext = {};
    if (manufacturer) ctx.manufacturer = manufacturer;
    if (fuelType !== 'unknown') ctx.fuelType = fuelType;
    return (dtcs ?? [])
      .map((dtc) => ({ dtc, info: explainDtc(dtc.code, ctx) }))
      .sort((a, b) => SEVERITY_RANK[b.info.severity] - SEVERITY_RANK[a.info.severity] || a.dtc.code.localeCompare(b.dtc.code));
  }, [dtcs, manufacturer, fuelType]);

  const run = useCallback(
    async (label: string, fn: () => Promise<void>) => {
      setBusy(label);
      setError(null);
      try {
        await fn();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusy(null);
      }
    },
    [],
  );

  const scan = (): void => {
    void run('Reading codes', async () => {
      const [codes, ready] = [await session.readDtcs(), await session.readReadiness()];
      setDtcs(codes);
      setReadiness(ready ?? null);
    });
  };

  const readFreeze = (): void => {
    void run('Reading freeze frame', async () => {
      const pids = [0x04, 0x05, 0x0b, 0x0c, 0x0d, 0x0e, 0x0f, 0x10, 0x11, 0x03];
      const raw = await session.readFreezeFrame(pids);
      const rows: { key: string; label: string; value: string }[] = [];
      for (const [pid, bytes] of raw) {
        const def = PID_BY_NUMBER.get(pid);
        if (!def) continue;
        const decoded = def.decode(bytes);
        for (const sig of def.signals) {
          rows.push({
            key: sig.key,
            label: sig.name,
            value: formatValue(decoded[sig.key], sig) + (sig.unit ? ' ' + sig.unit : ''),
          });
        }
      }
      setFreeze(rows);
    });
  };

  const clear = (): void => {
    setConfirmClear(false);
    void run('Clearing codes', async () => {
      await session.clearDtcs();
      setDtcs([]);
      setReadiness(null);
      setFreeze(null);
    });
  };

  if (!connected) {
    return (
      <div className="empty">
        <strong>Not connected</strong>
        <span>Connect to the car to read diagnostics.</span>
      </div>
    );
  }

  return (
    <div className="diag">
      <div className="diag-actions">
        <button className="btn" data-variant="primary" disabled={busy !== null} onClick={scan}>
          {busy === 'Reading codes' ? 'Reading…' : 'Scan for codes'}
        </button>
        <button className="btn" disabled={busy !== null} onClick={readFreeze}>
          Read freeze frame
        </button>
        <button
          className="btn"
          data-variant="danger"
          disabled={busy !== null || dtcs === null}
          onClick={() => setConfirmClear(true)}
        >
          Clear codes
        </button>
        {busy && <span className="muted">{busy}…</span>}
      </div>

      {error && <div className="card error-card">{error}</div>}

      {dtcs !== null && (
        <Summary explained={explained} readiness={readiness} manufacturer={manufacturer} vin={vin} />
      )}

      {dtcs !== null && explained.length > 0 && (
        <div className="card">
          <h3>Trouble codes</h3>
          {GROUPS.map((g) => (
            <DtcGroup
              key={g.status}
              title={g.title}
              tone={g.tone}
              hint={STATUS_EXPLANATIONS[g.status]}
              items={explained.filter((e) => e.dtc.status === g.status)}
              manufacturer={manufacturer}
            />
          ))}
        </div>
      )}

      {readiness && <Monitors readiness={readiness} />}

      {freeze && (
        <div className="card">
          <h3>Freeze frame</h3>
          <p className="muted small">
            A snapshot of engine conditions at the moment the first fault was recorded. It tells whether the
            fault appeared cold or warm, at idle or under load, which narrows down the cause.
          </p>
          {freeze.length === 0 ? (
            <p className="muted">No freeze frame stored.</p>
          ) : (
            <div className="freeze-grid">
              {freeze.map((row) => (
                <div key={row.key} className="freeze-row">
                  <span className="muted">{row.label}</span>
                  <span className="tnum">{row.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {confirmClear && (
        <Modal title="Clear trouble codes?" onClose={() => setConfirmClear(false)}>
          <p>
            This sends OBD-II mode 04. It erases stored codes and the freeze frame, and resets every
            readiness monitor. On many cars it also drops learned fuel trims, so it may run slightly
            differently until it relearns.
          </p>
          <p className="muted small">
            It does not fix anything. If the underlying fault is still there the code will come back,
            and clearing codes before an emissions test guarantees a fail on incomplete monitors.
            Permanent codes are not erased by this.
          </p>
          <div className="modal-actions">
            <button className="btn" onClick={() => setConfirmClear(false)}>Cancel</button>
            <button className="btn" data-variant="danger" onClick={clear}>Clear codes</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ summary

function Summary({
  explained, readiness, manufacturer, vin,
}: {
  explained: ExplainedDtc[];
  readiness: ReadinessResult | null;
  manufacturer: string | undefined;
  vin: string | undefined;
}): JSX.Element {
  const worst = explained[0];
  const count = (s: DtcSeverity): number => explained.filter((e) => e.info.severity === s).length;
  const stop = count('stop');
  const soon = count('soon');

  let verdict: { tone: 'critical' | 'warning' | 'good' | 'neutral'; text: string };
  if (explained.length === 0) {
    verdict = { tone: 'good', text: 'No trouble codes. The engine and emissions systems report no faults.' };
  } else if (stop > 0 && worst) {
    verdict = {
      tone: 'critical',
      text: 'Stop as soon as it is safe. ' + worst.info.code + ' (' + worst.info.title + ') can cause serious damage or be unsafe if you carry on.',
    };
  } else if (soon > 0) {
    verdict = {
      tone: 'warning',
      text: soon + (soon === 1 ? ' code needs' : ' codes need') + ' attention soon. The car can usually be driven gently until then.',
    };
  } else {
    verdict = { tone: 'neutral', text: 'Nothing urgent. Have the codes below checked when convenient.' };
  }

  return (
    <div className="card diag-summary" data-tone={verdict.tone}>
      <h3>Summary</h3>
      <p className="diag-verdict">{verdict.text}</p>
      <div className="kv-grid">
        {readiness && (
          <div className="kv">
            <span className="muted">Engine warning lamp</span>
            <span>{readiness.milOn ? 'on' : 'off'}</span>
          </div>
        )}
        <div className="kv">
          <span className="muted">Codes found</span>
          <span className="tnum">{explained.length}</span>
        </div>
        <div className="kv">
          <span className="muted">Explained for</span>
          <span>{manufacturer ? manufacturer + ' (from the VIN)' : vin ? 'this car (maker not recognised)' : 'any make'}</span>
        </div>
      </div>
      <p className="muted small" style={{ marginBottom: 0 }}>
        This reads what the engine and emissions modules report through the standard OBD-II interface, which is
        the same on every car. Brake (ABS), airbag and body modules usually keep their own codes, which need a
        scan tool for the make. If a red warning lamp is on, or the car behaves oddly, follow that before any
        code.
      </p>
    </div>
  );
}

// -------------------------------------------------------------------- codes

function DtcGroup({
  title, items, tone, hint, manufacturer,
}: {
  title: string;
  items: ExplainedDtc[];
  tone: string;
  hint: string;
  manufacturer: string | undefined;
}): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="dtc-group">
      <div className="dtc-group-head">
        {/* Status never rides on colour alone: the group is labelled too. */}
        <span className="pill" data-tone={tone}>{title}</span>
        <span className="muted small">{hint}</span>
      </div>
      {items.map((e) => (
        <DtcRow key={e.dtc.code + e.dtc.status} item={e} manufacturer={manufacturer} />
      ))}
    </div>
  );
}

function DtcRow({ item, manufacturer }: { item: ExplainedDtc; manufacturer: string | undefined }): JSX.Element {
  const { info } = item;
  const [open, setOpen] = useState(info.severity === 'stop');
  const detailsId = 'dtc-' + info.code + '-' + item.dtc.status;
  const lookUp = info.definedBy === 'manufacturer' || !info.titleKnown;
  const query = [info.definedBy === 'manufacturer' ? manufacturer : undefined, info.code, 'fault code']
    .filter(Boolean)
    .join(' ');

  return (
    <div className="dtc" data-severity={info.severity}>
      <button
        className="dtc-head"
        aria-expanded={open}
        aria-controls={detailsId}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="dtc-code tnum">{info.code}</span>
        <span className="dtc-title">
          {info.titleKnown ? info.title : info.definedBy === 'manufacturer' ? 'Carmaker-defined code' : 'Generic code, no name on file'}
          <span className="muted dtc-area">
            {info.area}
            {' · '}
            {info.definedBy === 'manufacturer' ? 'defined by ' + (manufacturer ?? 'the carmaker') : 'generic OBD-II'}
          </span>
        </span>
        <span className="pill dtc-severity" data-tone={SEVERITY_TONES[info.severity]}>{SEVERITY_LABELS[info.severity]}</span>
        <span className="dtc-chevron muted" aria-hidden>{open ? '▴' : '▾'}</span>
      </button>
      <p className="dtc-drive small">{info.drive}</p>

      {open && (
        <div className="dtc-details" id={detailsId}>
          <Section title="What it means">
            <p>{info.summary}</p>
            {info.faultType && <p className="muted">{info.faultType}</p>}
          </Section>
          <Section title="What you may notice">
            <List items={info.effects} />
          </Section>
          <Section title="Likely causes, most common first">
            <List items={info.causes} />
          </Section>
          <Section title="What to check">
            <List items={info.checks} />
          </Section>
          {lookUp && (
            <p className="small">
              <a
                href={'https://www.google.com/search?q=' + encodeURIComponent(query)}
                target="_blank"
                rel="noreferrer"
              >
                Look up {query} online
              </a>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="dtc-section">
      <h4 className="subhead">{title}</h4>
      {children}
    </div>
  );
}

function List({ items }: { items: string[] }): JSX.Element {
  return (
    <ul className="dtc-list">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

// ----------------------------------------------------------------- monitors

function Monitors({ readiness }: { readiness: ReadinessResult }): JSX.Element {
  const supported = readiness.monitors.filter((m) => m.supported);
  const incomplete = supported.filter((m) => !m.complete);
  return (
    <div className="card">
      <h3>Readiness monitors</h3>
      <p className="muted small">
        Self-tests the car runs as it is driven, here for a {readiness.ignition === 'compression' ? 'diesel (compression ignition)' : 'petrol (spark ignition)'} engine.
        Emissions inspections look for these to be complete. They reset when codes are cleared or the battery is
        disconnected, and a few days of mixed driving usually completes them.
        {incomplete.length > 0 && ' ' + incomplete.length + (incomplete.length === 1 ? ' has' : ' have') + ' not finished yet.'}
      </p>
      <div className="monitors">
        {supported.map((m) => (
          <div key={m.key} className="monitor">
            <span className={'monitor-dot' + (m.complete ? ' is-complete' : '')} />
            <span className="monitor-text">
              <span>{m.name}</span>
              <span className="muted small">{m.description}</span>
            </span>
            <span className="muted">{m.complete ? 'complete' : 'not ready'}</span>
          </div>
        ))}
        {supported.length === 0 && <p className="muted">This car does not report monitor status.</p>}
      </div>
    </div>
  );
}
